/* GoBuildIt — gbi-header.js (owner C1). <gbi-header>: condense on scroll, desktop mega + help
   disclosures (hover intent, click, keyboard), mobile menu drawer (GBI.dialog), layer state for the
   search (C2) and the scrim, --header-height for theme code. Spec: docs/specs/gbi-header-search-cart-spec.md §3.
   Vanilla ES2019. Depends on gbi-core.js (window.GBI) for dialog + track; degrades without it. */
(function () {
  'use strict';
  if (window.customElements.get('gbi-header')) return;

  var mqDesk = window.matchMedia('(min-width: 990px)');
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  function track(event, params) {
    try { if (window.GBI && typeof window.GBI.track === 'function') window.GBI.track(event, params || {}); } catch (e) { /* ignore */ }
  }
  function onMq(mq, fn) { if (mq.addEventListener) mq.addEventListener('change', fn); else mq.addListener(fn); }

  class GbiHeader extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this._off = [];                                   // document/window listeners, removed on disconnect
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.shell = this.querySelector('.gbi-hdr__shell');
      this.scrim = this.querySelector('.gbi-hdr__scrim');
      this.menu = this.querySelector('dialog.gbi-menu');
      this.search = this.querySelector('gbi-search');
      this.layers = new Set();                // 'mega' | 'help' | 'search'
      this.state = 'top';
      this.initHeights();
      this.initCondense();
      this.initTuck();
      this.initDisclosures();
      this.initMenu();
      this.initSearchBridge();
      this.initTracking();
      var self = this;
      this.listen(window, 'resize', function () { self.setHeight(); self.syncState(); }, { passive: true });
      // Back/forward cache restores the old scroll position without crossing a sentinel.
      this.listen(window, 'pageshow', function () { self.syncState(); });
      onMq(mqDesk, function () { if (!self.isConnected) return; self.closeAll(); self.placeSentinels(); self.syncState(); });
      this.listen(document, 'keydown', function (e) {
        if (e.key !== 'Escape' && e.key !== 'Esc') return;
        var openBtn = self.querySelector('[data-gbi-disclosure][aria-expanded="true"]');
        if (!openBtn) return;
        var panel = document.getElementById(openBtn.getAttribute('aria-controls'));
        var inside = panel && panel.contains(document.activeElement);
        self.closeDisclosures();
        if (inside || document.activeElement === openBtn) openBtn.focus();
      });
      // Theme editor: selecting a menu block opens the panel that shows it; deselecting closes it.
      if (window.Shopify && window.Shopify.designMode) {
        this.listen(document, 'shopify:block:select', function (e) {
          if (!self.contains(e.target)) return;
          var panel = e.target.closest && e.target.closest('#GbiMega, #GbiHelp');
          var btn = panel && self.querySelector('[aria-controls="' + panel.id + '"]');
          if (btn && mqDesk.matches) self.openDisclosure(btn, panel, btn.getAttribute('data-gbi-disclosure'), 'editor');
        });
        this.listen(document, 'shopify:block:deselect', function () { self.closeDisclosures(); });
      }
      // A click on the cart icon closes every header layer (the theme drawer opens on top).
      var cart = document.getElementById('cart-icon-bubble');
      if (cart) cart.addEventListener('click', function () { self.closeAll(); track('header_cart_click', {}); });
      this.listen(document, 'gbi:cart-added', function () {
        if (!cart) return;
        cart.classList.remove('is-bump');
        void cart.offsetWidth;
        cart.classList.add('is-bump');
      });
    }

    listen(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this._off.push(function () { target.removeEventListener(type, fn, opts); });
    }

    disconnectedCallback() {
      // Theme editor re-renders the section: drop global listeners, observers and sentinels of this instance.
      (this._off || []).forEach(function (off) { off(); });
      this._off = [];
      if (this._ro) this._ro.disconnect();
      if (this._io) this._io.disconnect();
      document.documentElement.classList.remove('gbi-hdr-tucked');
      [this.sDown, this.sUp].forEach(function (n) { if (n && n.parentNode) n.parentNode.removeChild(n); });
      document.documentElement.classList.remove('gbi-search-lock');
    }

    /* --------------------------------------------------------- height var */

    // Speed batch 2 · B1-7 (2026-10-17): at start-up the heights are read in the first ResizeObserver callback,
    // i.e. right after the browser's own first layout, instead of when this script runs (each read then forced an
    // extra full layout of the page while it was still loading). Same --header-height value (offsetHeight), read
    // once; later updates stay on the window resize and breakpoint paths, exactly as before. this.whenSized(fn)
    // runs fn after that first read (at once if it already happened, or at once without ResizeObserver).
    initHeights() {
      var self = this;
      this._sized = false;
      this._afterSize = [];
      // All reads first (header height, scroll position, then the announcement height in placeSentinels), all
      // writes after (sentinels, state, the CSS variable), so this callback never forces a layout either.
      var done = function () {
        if (self._sized) return;
        self._sized = true;
        if (self._ro) { self._ro.disconnect(); self._ro = null; }
        var h = self.section ? self.section.offsetHeight : self.offsetHeight;
        self._y0 = window.scrollY || 0;
        var list = self._afterSize; self._afterSize = [];
        list.forEach(function (fn) { fn(); });
        self.setHeight(h);
      };
      if (!('ResizeObserver' in window)) { done(); return; }
      this._ro = new ResizeObserver(function () { if (self.isConnected) done(); });
      this._ro.observe(this.section || this);
    }

    whenSized(fn) {
      if (this._sized) fn(); else this._afterSize.push(fn);
    }

    setHeight(known) {
      var h = known != null ? known : (this.section ? this.section.offsetHeight : this.offsetHeight);
      if (h) document.documentElement.style.setProperty('--header-height', h + 'px');
    }

    /* ----------------------------------------------------------- condense */

    initCondense() {
      if (this.getAttribute('data-sticky') === 'static') return;   // setting "Scroll away with the page"
      var self = this;
      var ann = this.querySelector('.gbi-hdr__ann');
      // Two sentinels in the page (not in the sticky header): condense past `down`, expand above `up`.
      var make = function (y) {
        var s = document.createElement('span');
        s.setAttribute('aria-hidden', 'true');
        s.className = 'gbi-hdr-sentinel';
        s.style.cssText = 'position:absolute;left:0;width:1px;height:1px;pointer-events:none;visibility:hidden;top:' + y + 'px';
        document.body.appendChild(s);
        return s;
      };
      this.placeSentinels = function () {
        self.annH = ann ? ann.offsetHeight : 0;          // 32 phone / 36 desktop
        self.downY = self.annH + (mqDesk.matches ? 72 : 56);
        self.upY = self.annH + 8;
        if (self.sDown) self.sDown.style.top = self.downY + 'px';
        if (self.sUp) self.sUp.style.top = self.upY + 'px';
      };
      this.whenSized(function () { self.startCondense(make); });
    }

    startCondense(make) {
      var self = this;
      this.placeSentinels();
      var restored = (this._y0 != null ? this._y0 : (window.scrollY || 0)) > this.downY;
      if ('IntersectionObserver' in window) {
        this.sDown = make(this.downY);
        this.sUp = make(this.upY);
        var io = this._io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            var above = !en.isIntersecting && en.boundingClientRect.top < 0;
            if (en.target === self.sDown && above) self.setState('condensed');
            if (en.target === self.sUp && en.isIntersecting) self.setState('top');
          });
        }, { threshold: 0 });
        io.observe(this.sDown);
        io.observe(this.sUp);
      } else {
        var ticking = false;
        this.listen(window, 'scroll', function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(function () {
            ticking = false;
            var y = window.scrollY;
            if (y > self.downY) self.setState('condensed'); else if (y < self.upY) self.setState('top');
          });
        }, { passive: true });
      }
      // Restored scroll position (back/forward): no animation on the first state.
      if (restored) this.setState('condensed', true);
    }

    placeSentinels() { /* replaced in initCondense; no-op for the static header */ }

    // The sentinels only report crossings. While frozen (a layer or the phone menu open) the page can scroll past
    // them unseen, so every unfreeze, resize and bfcache restore re-reads the scroll position.
    syncState() {
      if (this.getAttribute('data-sticky') === 'static' || this.downY == null || this.frozen()) return;
      var y = window.scrollY || window.pageYOffset || 0;
      var next = y > this.downY ? 'condensed' : (y < this.upY ? 'top' : this.state);
      this.setState(next, true);
    }

    setState(next, instant) {
      if (next === this.state || this.frozen()) return;
      var self = this;
      var apply = function () {
        self.state = next;
        self.setAttribute('data-state', next);
        if (self.section) self.section.classList.toggle('is-condensed', next === 'condensed');
        if (next === 'top') self.setTucked(false);
      };
      if (instant || mqReduce.matches) { apply(); return; }
      // The search fades out, the layout swaps, the search fades in (no movement of the page).
      this.classList.add('is-swapping');
      clearTimeout(this._swap);
      this._swap = setTimeout(function () {
        apply();
        requestAnimationFrame(function () { self.classList.remove('is-swapping'); });
      }, 140);
    }

    // No state change while Shop, Help, the search or the phone menu is open (a wheel tick over an open mega
    // used to condense the header and close the menu under the pointer).
    frozen() { return this.layers.size > 0 || !!(this.menu && this.menu.open); }

    /* --------------------------------------------------------------- tuck */

    // Phones: the floating bar hides on scroll-down and returns on scroll-up (one passive, rAF-throttled listener).
    initTuck() {
      if (this.getAttribute('data-sticky') === 'static') return;
      var self = this;
      var lastY = null;
      var ticking = false;
      this.whenSized(function () { if (lastY == null) lastY = self._y0 != null ? self._y0 : (window.scrollY || 0); });
      this.listen(window, 'scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var y = window.scrollY || 0;
          if (lastY == null) lastY = 0;
          var dy = y - lastY;
          if (Math.abs(dy) < 8) return;                     // ignore jitter; small moves accumulate
          lastY = y;
          self.tuck(dy > 0 && y > (self.downY || 0) + 56);
        });
      }, { passive: true });
    }

    hasKeyboardFocus() {
      try { return !!this.querySelector(':focus-visible'); } catch (e) { return false; }
    }

    tuck(on) {
      if (on && (mqDesk.matches || this.state !== 'condensed' || this.frozen() || this.hasKeyboardFocus())) on = false;
      this.setTucked(on);
    }

    // is-tucked on the header, mirrored as html.gbi-hdr-tucked (B1-7) so page CSS can follow it without a
    // whole-page :has() rule. Every change of is-tucked goes through here.
    setTucked(on) {
      this.classList.toggle('is-tucked', on);
      document.documentElement.classList.toggle('gbi-hdr-tucked', !!on);
    }

    /* -------------------------------------------------------- disclosures */

    initDisclosures() {
      var self = this;
      this.querySelectorAll('[data-gbi-disclosure]').forEach(function (btn) {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;
        var name = btn.getAttribute('data-gbi-disclosure');
        var item = btn.parentElement;
        var openT = null;
        var closeT = null;
        var openedHow = '';
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          clearTimeout(openT);
          var isOpen = btn.getAttribute('aria-expanded') === 'true';
          // Opened by hover: the click that usually follows keeps it open (the next click closes it).
          if (isOpen && openedHow === 'hover') { openedHow = 'click'; return; }
          if (isOpen) self.closeDisclosures();
          else { self.openDisclosure(btn, panel, name, 'click'); openedHow = 'click'; }
        });
        btn.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            self.openDisclosure(btn, panel, name, 'key');
            openedHow = 'key';
            var first = panel.querySelector('a[href], button');
            if (first) first.focus();
          }
        });
        if (mqHover.matches) {
          var enter = function () {
            clearTimeout(closeT);
            if (btn.getAttribute('aria-expanded') === 'true') return;
            // 180 ms: a pointer crossing the nav on its way down the page does not drop the panel.
            openT = setTimeout(function () { self.openDisclosure(btn, panel, name, 'hover'); openedHow = 'hover'; }, 180);
          };
          var leave = function () {
            clearTimeout(openT);
            closeT = setTimeout(function () {
              if (btn.getAttribute('aria-expanded') === 'true' && !item.matches(':hover') && !panel.matches(':hover')) self.closeDisclosures();
            }, 260);
          };
          btn.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') enter(); });
          btn.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') leave(); });
          panel.addEventListener('pointerenter', function () { clearTimeout(closeT); });
          panel.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') leave(); });
        }
        item.addEventListener('focusout', function (e) {
          if (e.relatedTarget && !item.contains(e.relatedTarget) && btn.getAttribute('aria-expanded') === 'true') self.closeDisclosures();
        });
      });
      if (this.scrim) this.scrim.addEventListener('click', function () { self.closeAll(); });
      this.listen(document, 'pointerdown', function (e) {
        if (!self.querySelector('[data-gbi-disclosure][aria-expanded="true"]')) return;
        if (e.target.closest && e.target.closest('.gbi-hdr__nav')) return;
        if (self.scrim && e.target === self.scrim) return;
        self.closeDisclosures();
      });
    }

    openDisclosure(btn, panel, name, how) {
      this.closeDisclosures(btn);
      if (this.search && this.search.isOpen) this.search.close({ reason: 'menu' });
      // Warm the thumbnails (lazy images inside a hidden panel never load before the first open).
      panel.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
      panel.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      this.layer(name, true);
      track('nav_open', { menu: name, how: how, state: this.state });
    }

    closeDisclosures(except) {
      var self = this;
      this.querySelectorAll('[data-gbi-disclosure][aria-expanded="true"]').forEach(function (b) {
        if (b === except) return;
        b.setAttribute('aria-expanded', 'false');
        var p = document.getElementById(b.getAttribute('aria-controls'));
        if (p) p.hidden = true;
        self.layer(b.getAttribute('data-gbi-disclosure'), false);
      });
    }

    closeAll() {
      this.closeDisclosures();
      if (this.search && this.search.isOpen) this.search.close({ reason: 'header' });
    }

    layer(name, on) {
      if (on) this.layers.add(name); else this.layers.delete(name);
      var any = this.layers.size > 0;
      this.classList.toggle('is-layer-open', any);
      this.classList.toggle('is-search-open', this.layers.has('search'));
      if (this.section) this.section.classList.toggle('is-layer-open', any);
      if (this.scrim) this.scrim.hidden = !(any && (mqDesk.matches || !this.layers.has('search')));
      if (any) this.setTucked(false);
      else if (!this._syncing) { this._syncing = true; this.syncState(); this._syncing = false; }
    }

    /* ---------------------------------------------------------- search bridge */

    initSearchBridge() {
      var self = this;
      this.addEventListener('gbi:search-open', function () { self.closeDisclosures(); self.layer('search', true); });
      this.addEventListener('gbi:search-close', function () { self.layer('search', false); });
      this.querySelectorAll('[data-gbi-hdr-search-open]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (self.search && typeof self.search.open === 'function') self.search.open({ source: 'icon' });
        });
      });
    }

    /* --------------------------------------------------------- mobile menu */

    initMenu() {
      var self = this;
      var menu = this.menu;
      if (!menu) return;
      var burger = this.querySelector('[data-gbi-menu-open]');
      if (burger) burger.addEventListener('click', function () {
        self.closeAll();
        if (window.GBI && window.GBI.dialog) window.GBI.dialog.open(menu, { opener: burger, history: true });
        else { try { menu.showModal(); } catch (e) { menu.setAttribute('open', ''); } menu.classList.add('is-open'); }
        burger.setAttribute('aria-expanded', 'true');
        track('menu_open', {});
      });
      var l1 = menu.querySelector('#GbiMenuL1');
      menu.addEventListener('gbi:dialog-close', function () {
        if (burger) burger.setAttribute('aria-expanded', 'false');
        menu.querySelectorAll('.gbi-menu__level--sub.is-active').forEach(function (l) { l.classList.remove('is-active'); });
        menu.querySelectorAll('[data-gbi-menu-push][aria-expanded="true"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
        if (l1) l1.inert = false;
        self.syncState();
      });
      menu.addEventListener('click', function (e) {
        // A menu link replaces the history entry GBI.dialog pushed for the back button, so Back after
        // navigating needs one press, not two.
        var link = e.target.closest('a[href]');
        if (link && !e.defaultPrevented && e.button === 0 && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) &&
            (!link.target || link.target === '_self') && link.origin === window.location.origin &&
            window.history.state && window.history.state.gbiDialog) {
          e.preventDefault();
          window.location.replace(link.href);
          return;
        }
        var push = e.target.closest('[data-gbi-menu-push]');
        if (push) {
          var level = menu.querySelector('#' + push.getAttribute('data-gbi-menu-push'));
          if (!level) return;
          level.classList.add('is-active');
          level._opener = push;
          push.setAttribute('aria-expanded', 'true');
          if (l1) l1.inert = true;                         // the level underneath leaves the tab order and the a11y tree
          var title = level.querySelector('.gbi-menu__title');
          setTimeout(function () { if (title) title.focus({ preventScroll: true }); }, mqReduce.matches ? 0 : 60);
          track('menu_level', { level: push.getAttribute('data-gbi-level') || '' });
          return;
        }
        var back = e.target.closest('[data-gbi-menu-back]');
        if (back) {
          var lv = back.closest('.gbi-menu__level--sub');
          if (l1) l1.inert = false;
          if (lv) {
            lv.classList.remove('is-active');
            if (lv._opener) { lv._opener.setAttribute('aria-expanded', 'false'); lv._opener.focus({ preventScroll: true }); }
          }
          return;
        }
        var s = e.target.closest('[data-gbi-menu-search]');
        if (s) {
          // Everything in this click: iOS only raises the keyboard for a focus() inside the user gesture.
          // GBI.dialog.close() marks the dialog as closing at once and cleans up later (history entry, scroll lock);
          // the native close() leaves the top layer now, so the page is no longer inert and the field can take focus.
          if (window.GBI && window.GBI.dialog) window.GBI.dialog.close(menu, { reason: 'api', restoreFocus: false });
          try { if (menu.open) menu.close(); } catch (err) { menu.removeAttribute('open'); }
          if (self.search) self.search.open({ source: 'menu' });
        }
      });
    }

    /* ------------------------------------------------------------ tracking */

    initTracking() {
      this.addEventListener('click', function (e) {
        var a = e.target.closest && e.target.closest('a[data-gbi-nav]');
        if (!a) return;
        var p = a.getAttribute('data-gbi-nav').split(':');
        track('nav_click', { menu: p[0] || '', group: p[1] || '', item: p[2] || '', position: Number(p[3]) || 0 });
      });
    }
  }

  /* ---------------------------------------------------- <gbi-ann> announcement bars (2026-10-20) */
  // Bars that are on take turns every data-interval seconds. Waits while hovered or focused and while the tab is
  // hidden; in the theme editor the selected bar shows and stays. One bar: nothing runs.

  class GbiAnn extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.gbi-hdr__ann-slide'));
      if (this.slides.length < 2) return;
      this._ready = true;
      this.i = 0;
      this.ms = Math.max(3, Number(this.getAttribute('data-interval')) || 5) * 1000;
      this.hold = {};
      var self = this;
      var hold = function (key, on) { return function () { self.hold[key] = on; self.run(); }; };
      this.addEventListener('mouseenter', hold('hover', true));
      this.addEventListener('mouseleave', hold('hover', false));
      this.addEventListener('focusin', hold('focus', true));
      this.addEventListener('focusout', hold('focus', false));
      this._onVis = function () { self.hold.hidden = document.hidden; self.run(); };
      this._onSel = function (e) {
        var n = self.slides.indexOf(e.target);
        if (n < 0) return;
        self.show(n);
        self.hold.editor = true;
        self.run();
      };
      this._onDesel = function (e) { if (self.slides.indexOf(e.target) < 0) return; self.hold.editor = false; self.run(); };
      document.addEventListener('visibilitychange', this._onVis);
      document.addEventListener('shopify:block:select', this._onSel);
      document.addEventListener('shopify:block:deselect', this._onDesel);
      this.run();
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      clearInterval(this.t);
      document.removeEventListener('visibilitychange', this._onVis);
      document.removeEventListener('shopify:block:select', this._onSel);
      document.removeEventListener('shopify:block:deselect', this._onDesel);
    }

    run() {
      clearInterval(this.t);
      var h = this.hold;
      if (h.hover || h.focus || h.hidden || h.editor) return;
      var self = this;
      this.t = setInterval(function () { self.show((self.i + 1) % self.slides.length); }, this.ms);
    }

    show(n) {
      if (n === this.i) return;
      this.slides[this.i].classList.remove('is-active');
      this.slides[n].classList.add('is-active');
      this.i = n;
    }
  }

  window.customElements.define('gbi-header', GbiHeader);
  if (!window.customElements.get('gbi-ann')) window.customElements.define('gbi-ann', GbiAnn);
})();
